import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { IconButton, Table, TableBody, TableHead, TableRow, CircularProgress } from '@mui/material';
import { useState } from 'react';

import { GeneralTableBox, HeadTableCell, RowTableCell, NoDataSkeleton } from '../../../common';
import { ConfirmationDialog } from '../../../common/components';
import HoverExpandableAmmounts from './HoverExpandableAmmounts';
import HoverExpandableSubscribers from './HoverExpandableSubscribers';

const CouponsDataTable = ({
  data,
  isLoading,
  onEdit,
  onDelete,
  onDownload,
  onRegenerate,
  regeneratingId,
  LoadingComponent,
}) => {
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    couponId: null,
    couponInfo: null
  });
  const [regenerateDialog, setRegenerateDialog] = useState({
    open: false,
    couponId: null,
    couponInfo: null,
  });

  const handleDeleteClick = (couponId, couponInfo) => {
    setDeleteDialog({
      open: true,
      couponId,
      couponInfo
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      couponId: null,
      couponInfo: null
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.couponId) {
      onDelete(deleteDialog.couponId);
      handleDeleteCancel();
    }
  };

  const handleRegenerateClick = (couponId, couponInfo) => {
    setRegenerateDialog({
      open: true,
      couponId,
      couponInfo,
    });
  };

  const handleRegenerateCancel = () => {
    setRegenerateDialog({
      open: false,
      couponId: null,
      couponInfo: null,
    });
  };

  const handleRegenerateConfirm = () => {
    if (regenerateDialog.couponId && onRegenerate) {
      onRegenerate(regenerateDialog.couponId);
      handleRegenerateCancel();
    }
  };

  return (
    <>
      <GeneralTableBox>
      <Table aria-label="coupons table">
        <TableHead sx={{ fontSize: '10px' }}>
          <TableRow>
            <HeadTableCell wxs="15%" wmd="10%">
              Orden
            </HeadTableCell>
            <HeadTableCell wxs="20%" wmd="10%">
              Suscriptores
            </HeadTableCell>
            <HeadTableCell wxs="10%" wmd="10%">
              Mes
            </HeadTableCell>
            <HeadTableCell wxs="10%" wmd="10%">
              Año
            </HeadTableCell>
            <HeadTableCell wxs="15%" wmd="10%">
              Cuotas
            </HeadTableCell>
            <HeadTableCell wxs="25%" wmd="10%">
              Importes
            </HeadTableCell>
            <HeadTableCell wxs="15%" wmd="10%">
              Cancelado
            </HeadTableCell>
            <HeadTableCell wxs="10%" wmd="10%">
              Regenerar QR
            </HeadTableCell>
            <HeadTableCell wxs="10%" wmd="10%">
              Editar
            </HeadTableCell>
            <HeadTableCell wxs="10%" wmd="10%">
              Eliminar
            </HeadTableCell>
            <HeadTableCell>Descargar</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <LoadingComponent />
          ) : data && data.length > 0 ? (
            data.map((row, index) => (
              <TableRow key={index}>
                <RowTableCell>{row.orden}</RowTableCell>

                <RowTableCell sx={{ whiteSpace: 'nowrap', overflow: 'visible' }}>
                  <HoverExpandableSubscribers subscribers={row.suscriptores} />
                </RowTableCell>

                <RowTableCell>{row.mes}</RowTableCell>

                <RowTableCell>{row.año}</RowTableCell>

                <RowTableCell>{row.cuotas}</RowTableCell>

                <RowTableCell>
                  <HoverExpandableAmmounts items={row.importes} />
                </RowTableCell>

                <RowTableCell>{row.cancelado}</RowTableCell>
                <RowTableCell displayxs="none" displaymd="table-cell">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => handleRegenerateClick(row.uuid, row)}
                    disabled={row.isCancelled || !onRegenerate || regeneratingId === row.uuid}
                  >
                    {regeneratingId === row.uuid ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AutorenewIcon fontSize="small" />
                    )}
                  </IconButton>
                </RowTableCell>
                <RowTableCell displayxs="none" displaymd="table-cell">
                  {!row.isCancelled && (
                    <IconButton size="small" color="primary" onClick={() => onEdit(row.uuid, row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </RowTableCell>

                <RowTableCell displayxs="none" displaymd="table-cell">
                  {row.can_delete === true && (
                    <IconButton size="small" color="tertiary" onClick={() => handleDeleteClick(row.uuid, row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </RowTableCell>

                <RowTableCell>
                  <IconButton size="small" color="primary" onClick={() => onDownload(row.uuid)}>
                    <DownloadIcon fontSize="small"/>
                  </IconButton>
                </RowTableCell>
              </TableRow>
            ))
          ) : (
            <NoDataSkeleton colSpan={11} message="No se encontraron cupones" />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>

    <ConfirmationDialog
      open={deleteDialog.open}
      onClose={handleDeleteCancel}
      onConfirm={handleDeleteConfirm}
      title="Confirmar eliminación"
      message={`¿Está seguro que desea eliminar el cupón de contrato número ${deleteDialog.couponInfo?.orden} del período ${deleteDialog.couponInfo?.mes}/${deleteDialog.couponInfo?.año}?`}
      entityName="cupones"
      confirmText="Eliminar cupón"
      cancelText="Cancelar"
    />

    <ConfirmationDialog
      open={regenerateDialog.open}
      onClose={handleRegenerateCancel}
      onConfirm={handleRegenerateConfirm}
      title="Regenerar códigos QR"
      message={
        `Una vez regenerados los QR, los anteriores quedaran inutilizables. ` +
        `Ten en cuenta que si alguien paga con un QR anterior el cobro no se registrara porque fue reemplazado.`
      }
      entityName="cupones"
      confirmText="Regenerar QRs"
      cancelText="Cancelar"
      confirmVariant="warning"
    />
    </>
  );
};

export default CouponsDataTable;
